// Phase 18 Plan 03 — POLISH-08 RootApp wrapper.
// Phase 18 Plan 04 — POLISH-07 global <LiveRegion /> mount.
// Phase 18 Plan 06 — POLISH-11 $fontScale / $typography / $contrast dataset
//                    mirrors (text-size + OpenDyslexic + high-contrast toggles).
//
// Wraps the Router + Footer to mount document-level a11y effects in a single
// seam:
//   - $locale → documentElement.lang mirror (POLISH-08 sub-deliverable 1)
//   - $fontScale → documentElement.dataset.fontScale mirror (POLISH-11)
//   - $typography → documentElement.dataset.typography mirror (POLISH-11)
//   - $contrast → documentElement.dataset.contrast mirror (POLISH-11)
//   - <SkipLink /> as the first focusable element (POLISH-08 sub-deliverable 3)
//   - <LiveRegion /> two sr-only aria-live regions (polite + assertive)
//     mounted at page load so screen readers compile the tracker before any
//     route announces (Plan 18-04 / POLISH-07; useAnnounce consumes them).
//
// Mounted from main.tsx INSIDE the provider stack (Intl/Theme/Motion/Auth)
// so the SkipLink can read i18n keys and the lang mirror runs only once.
//
// CONTEXT.md Area 2 Q3 — useStore re-renders the component on locale change,
// the useEffect mirrors the value to documentElement.lang. The lang attribute
// on the static `index.html` (line 2: lang="en") is the pre-hydration baseline;
// this effect updates it after React mounts. The three POLISH-11 dataset
// mirrors follow the identical pattern — they ALSO take effect post-hydration
// (the static index.html has no data-font-scale / data-typography /
// data-contrast attributes, so default CSS values cover the pre-hydration
// paint window).

import { useStore } from '@nanostores/react'
import { type JSX, useEffect } from 'react'

import { useAuth } from '../auth/AuthProvider.js'
import { Router } from '../router/router.js'
import { routes } from '../router/routes.js'
import { $contrast } from '../stores/contrast.js'
import { $fontScale } from '../stores/fontScale.js'
import { $locale } from '../stores/locale.js'
import { $typography } from '../stores/typography.js'
import { LiveRegion } from '../ui/components/LiveRegion.js'
import { SkipLink } from '../ui/components/SkipLink.js'
import { Footer } from '../ui/footer/Footer.js'
import { useAudioRoot } from './audio/useAudio.js'

export function RootApp(): JSX.Element {
  // $locale → documentElement.lang mirror (POLISH-08).
  const locale = useStore($locale)
  // POLISH-11 a11y stretch toggles — three persistent atoms mirrored to
  // documentElement.dataset for CSS [data-*] keying.
  const fontScale = useStore($fontScale)
  const typography = useStore($typography)
  const contrast = useStore($contrast)
  // ARCA-92 / WS1 — feed the resolved auth state into the router-level auth
  // guard. RootApp sits inside <AuthProvider> (main.tsx), so it can read
  // useAuth() and pass it down to the Router (which sits below the provider).
  const auth = useAuth()

  // Polish Phase 1 — bind the procedural audio engine to its prefs + theme and
  // wire first-gesture unlock, once at the app root.
  useAudioRoot()

  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  useEffect(() => {
    document.documentElement.dataset.fontScale = fontScale
  }, [fontScale])

  useEffect(() => {
    document.documentElement.dataset.typography = typography
  }, [typography])

  useEffect(() => {
    document.documentElement.dataset.contrast = contrast
  }, [contrast])

  return (
    <>
      <SkipLink />
      <LiveRegion />
      <Router routes={routes} authKind={auth.kind} />
      {/* Plan 08-07 — site-wide Footer with SALDO + SCOWL attribution.
          Lives outside the Router so it renders on every page. */}
      <Footer />
    </>
  )
}
